import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import URI from 'urijs';
import { t } from 'Src/i18n';
import { classnames } from 'Utils/classnames/classnames.utils';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { useAppContext } from 'Context/ReactAppContext';
import {
  getCurrentProposal,
  getFilteredAccommodationsAlternativesByComfort,
  getProposals,
} from 'Store/booking/booking.selector';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import type { AccommodationRoomInfosThemeKey, PopinType } from 'Src/types/reservation';
import { Button } from 'Components/frameworks/button/Button';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Scrollable } from 'Components/frameworks/scrollable/Scrollable';
import { FiltersPopin } from './FiltersPopin';
import { popinTypes } from './constants';
import { themes } from './AccommodationRoomInfos.themes';
import {
  isItemFilterable,
  isRoomTypeItem,
  isViewItem,
  isOutsideFacilityItem,
  hasTypedAlternative,
} from './AccommodationRoomInfos.utils';

const WIDTH = 108;

type Props = {
  className?: string;
  isExclusiveCollection?: boolean;
  isFilterableInfoList?: boolean;
  isInMoreRoomLayer: boolean;
  isAccommodationSummary: boolean;
  isScrollable?: boolean;
  items: Array<{
    buttonText?: string;
    icon: IconName;
    labels: Array<string>;
  }>;
  proposal?: Proposal | null | undefined;
  roomCategoryId?: string;
  theme?: AccommodationRoomInfosThemeKey;
};

export const AccommodationRoomInfos = ({
  className,
  isExclusiveCollection = false,
  isFilterableInfoList = false,
  isInMoreRoomLayer,
  isAccommodationSummary,
  isScrollable = true,
  items = [],
  proposal,
  roomCategoryId = '',
  theme = 'default',
}: Props) => {
  const selectedProposal = useSelector(getCurrentProposal);
  const alternatives = useSelector(getFilteredAccommodationsAlternativesByComfort);
  const currentComfortAlternatives = alternatives?.find(({ categoryId }) => {
    return categoryId === roomCategoryId;
  });
  const { trackClickEvent } = useTrackEvent();
  const [popinType, setPopinType] = useState<PopinType | null>(null);
  const router = useRouter();
  const isNotComparatorView = router.pathname !== '/comparator';
  const notComparatorNorLayer = !isInMoreRoomLayer && isNotComparatorView;
  const proposals = useSelector(getProposals);
  const selectedProposalId = isNotComparatorView
    ? router.query.selectedProposalId
    : selectedProposal.id;
  const selectedProposalForFiltersPopin = notComparatorNorLayer
    ? proposals.find(({ id }) => id === router.query.selectedProposalId)
    : selectedProposal;
  const { thTitle, thIcon, thIconWidth, thItem, thList, thListItem, thText, thCTA } =
    themes(isExclusiveCollection)[theme];
  const [isOpenedPopIn, setIsOpenedPopIn] = React.useState(false);

  const closePopIn = () => setIsOpenedPopIn(false);

  const openPopIn = (icon: IconName) => {
    if (isRoomTypeItem(icon)) {
      trackClickEvent(TRACK_EVENT_NAMES.CLICK_ACCOMMODATION, {
        detail_click: 'room_perso_room_configuration',
      });
      setPopinType(popinTypes.ROOM_TYPE);
    }

    if (isViewItem(icon)) {
      trackClickEvent(TRACK_EVENT_NAMES.CLICK_ACCOMMODATION, {
        detail_click: 'room_perso_room_view',
      });
      setPopinType(popinTypes.VIEW);
    }

    if (isOutsideFacilityItem(icon)) {
      trackClickEvent(TRACK_EVENT_NAMES.CLICK_ACCOMMODATION, {
        detail_click: 'room_perso_room_facilities',
      });
      setPopinType(popinTypes.OUTSIDE_FACILITIES);
    }

    setIsOpenedPopIn(true);
  };

  const uri = new URI(router.asPath);
  const pathname = uri.pathname();
  const isOption = pathname.startsWith('/option');
  const isConfirmation = pathname === '/confirmation';
  const appContext = useAppContext();
  const isAlternativeFiltersModeActive = isFeatureFlipped(appContext)(
    'booking.AlternativeFiltersModeActive',
  );
  const shouldShowPopin =
    notComparatorNorLayer &&
    isFilterableInfoList &&
    isOpenedPopIn &&
    isAlternativeFiltersModeActive &&
    proposal?.id === selectedProposalId;
  const hasAtLeastOneAlternativeOfAnyType = items.reduce((prev, { icon }) => {
    return (
      prev ||
      (isItemFilterable(icon) &&
        hasTypedAlternative({
          currentComfortAlternatives,
          icon,
          proposal,
        }))
    );
  }, false);
  const shouldShowPersonalizeYourRoomTitle =
    notComparatorNorLayer &&
    isAlternativeFiltersModeActive &&
    hasAtLeastOneAlternativeOfAnyType &&
    isFilterableInfoList;

  const content = (
    <React.Fragment>
      {shouldShowPopin && selectedProposalForFiltersPopin && popinType && (
        <FiltersPopin
          onClose={closePopIn}
          proposal={selectedProposalForFiltersPopin}
          type={popinType}
        />
      )}
      {isExclusiveCollection && (
        <h3 className={thTitle}>{t('booking.accommodation.exclusiveCollectionServices')}</h3>
      )}

      {shouldShowPersonalizeYourRoomTitle && (
        <div className="mb-10 font-brand text-16">
          {t('booking.accommodation.roomFinder.filtersTitle')}
        </div>
      )}
      <ul className={thList}>
        {items.map(({ icon, labels = [], buttonText = undefined }, idx) => {
          return (
            <li
              className={thListItem}
              key={idx}
              style={{
                width:
                  (!isExclusiveCollection &&
                    (isOption || isConfirmation || isAccommodationSummary)) ||
                  !isScrollable
                    ? null
                    : WIDTH,
              }}
            >
              <div className={thItem}>
                <div
                  className={classnames({
                    'flex-1': isOption || isConfirmation || isAccommodationSummary,
                    flex: isOption || isConfirmation || isAccommodationSummary,
                  })}
                >
                  <Icon className={thIcon} name={icon} width={thIconWidth} />
                  {labels.map((label) => (
                    <div className={thText} key={label}>
                      {label}
                    </div>
                  ))}
                </div>
                {isFilterableInfoList &&
                  notComparatorNorLayer &&
                  isAlternativeFiltersModeActive &&
                  isItemFilterable(icon) &&
                  hasTypedAlternative({
                    currentComfortAlternatives,
                    icon,
                    proposal,
                  }) && (
                    <Button className={thCTA} onClick={() => openPopIn(icon)} theme="link">
                      {buttonText || t('booking.common.change')}
                    </Button>
                  )}
              </div>
            </li>
          );
        })}
      </ul>
    </React.Fragment>
  );

  if (isScrollable) {
    return (
      <Scrollable
        backwardLabel={t('common.scrollBackward')}
        className={className}
        deltaScrollFactorClick={WIDTH}
        elementWidth={WIDTH}
        forwardLabel={t('common.scrollForward')}
        navPosition="center"
      >
        {content}
      </Scrollable>
    );
  }

  return <div className={className}>{content}</div>;
};
